import { RootShell } from '@/components/layout/RootShell';
import { RouteErrorPage } from '@/components/layout/RouteErrorPage';
import { createRootRoute, createRoute, createRouter, redirect } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { lazy } from 'react';

/**
 * Code splitting por rota via `React.lazy`.
 *
 * Cada `lazy()` cria um chunk separado no Rollup. O `<Suspense>` que envolve
 * a área de conteúdo está em `AppShell` (não aqui) — fallback genérico de
 * skeleton enquanto o chunk baixa.
 *
 * **Efeito**: bundle inicial cai de ~1.1MB pra ~200-400KB. Cada rota baixa
 * sob demanda na primeira navegação. Subsequentes ficam cacheadas pelo
 * browser. `defaultPreload: 'intent'` (abaixo) faz prefetch no hover do link
 * — usuário não percebe a latência depois do primeiro acesso.
 *
 * **Trade-off**: `index` (Dashboard) usa `lazy` igual aos outros pra manter
 * consistência. Alternativa seria importar Dashboard direto (eager) pra que
 * o landing não tenha flash de fallback. Optei por manter tudo lazy — o
 * fallback é discreto e o ganho de consistência supera o flash.
 */

const DashboardPage = lazy(() =>
  import('@/routes/dashboard').then((m) => ({ default: m.DashboardPage })),
);
const AnalyticsPage = lazy(() =>
  import('@/routes/analytics').then((m) => ({ default: m.AnalyticsPage })),
);
const ConnectionsPage = lazy(() =>
  import('@/routes/connections').then((m) => ({ default: m.ConnectionsPage })),
);
const ConnectionDetailPage = lazy(() =>
  import('@/components/connections/ConnectionDetail').then((m) => ({
    default: m.ConnectionDetailPage,
  })),
);
const TemplatesPage = lazy(() =>
  import('@/routes/templates').then((m) => ({ default: m.TemplatesPage })),
);
const TemplateDetailPage = lazy(() =>
  import('@/components/templates/TemplateDetail').then((m) => ({
    default: m.TemplateDetailPage,
  })),
);
const MessagesPage = lazy(() =>
  import('@/routes/messages').then((m) => ({ default: m.MessagesPage })),
);
const MessageDetailPage = lazy(() =>
  import('@/components/messages/MessageDetail').then((m) => ({ default: m.MessageDetailPage })),
);
const InboxPage = lazy(() => import('@/routes/inbox').then((m) => ({ default: m.InboxPage })));
const LeadsPage = lazy(() => import('@/routes/leads').then((m) => ({ default: m.LeadsPage })));
const LeadDetailPage = lazy(() =>
  import('@/components/leads/LeadDetail').then((m) => ({ default: m.LeadDetailPage })),
);
const CadencesPage = lazy(() =>
  import('@/routes/cadences').then((m) => ({ default: m.CadencesPage })),
);
const CadenceDetailPage = lazy(() =>
  import('@/components/cadences/CadenceDetail').then((m) => ({ default: m.CadenceDetailPage })),
);
const CampaignsPage = lazy(() =>
  import('@/routes/campaigns').then((m) => ({ default: m.CampaignsPage })),
);
const CampaignDetailPage = lazy(() =>
  import('@/components/campaigns/CampaignDetail').then((m) => ({
    default: m.CampaignDetailPage,
  })),
);
const AIAgentsPage = lazy(() =>
  import('@/routes/ai-agents').then((m) => ({ default: m.AIAgentsPage })),
);
const AIAgentDetailPage = lazy(() =>
  import('@/components/ai-agents/AIAgentDetail').then((m) => ({ default: m.AIAgentDetailPage })),
);
const WebhooksPage = lazy(() =>
  import('@/routes/webhooks').then((m) => ({ default: m.WebhooksPage })),
);
const AccountShellPage = lazy(() =>
  import('@/routes/account-shell').then((m) => ({ default: m.AccountShellPage })),
);
const AccountProfilePage = lazy(() =>
  import('@/routes/account-profile').then((m) => ({ default: m.AccountProfilePage })),
);
const AccountSecurityPage = lazy(() =>
  import('@/routes/account-security').then((m) => ({ default: m.AccountSecurityPage })),
);
const AccountTenantPage = lazy(() =>
  import('@/routes/account-tenant').then((m) => ({ default: m.AccountTenantPage })),
);
const AccountIntegrationsPage = lazy(() =>
  import('@/routes/account-integrations').then((m) => ({
    default: m.AccountIntegrationsPage,
  })),
);
// AccountWebhookSecretsPage foi removida — webhook-secrets agora vive como
// tab dentro de /account/integrations (consolidação RF3 do refactor de
// modularização). A rota `/account/webhook-secrets` continua válida via
// redirect abaixo pra não quebrar bookmarks antigos.
const BusinessManagersPage = lazy(() =>
  import('@/routes/business-managers').then((m) => ({ default: m.BusinessManagersPage })),
);
const BusinessManagerDetailPage = lazy(() =>
  import('@/components/business-managers/BusinessManagerDetail').then((m) => ({
    default: m.BusinessManagerDetailPage,
  })),
);
const RegistrationsPage = lazy(() =>
  import('@/routes/registrations').then((m) => ({ default: m.RegistrationsPage })),
);

const LoginPage = lazy(() => import('@/routes/login').then((m) => ({ default: m.LoginPage })));
const SignupPage = lazy(() => import('@/routes/signup').then((m) => ({ default: m.SignupPage })));
const ForgotPasswordPage = lazy(() =>
  import('@/routes/forgot-password').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import('@/routes/reset-password').then((m) => ({ default: m.ResetPasswordPage })),
);
const VerifyEmailPage = lazy(() =>
  import('@/routes/verify-email').then((m) => ({ default: m.VerifyEmailPage })),
);

const rootRoute = createRootRoute({
  component: () => (
    <>
      <RootShell />
      {import.meta.env.DEV ? <TanStackRouterDevtools position="bottom-right" /> : null}
    </>
  ),
});

// Public routes (login/signup) — sem AppShell, sem AuthGate.
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  component: SignupPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
  validateSearch: (search: Record<string, unknown>): { token?: string } => {
    // exactOptionalPropertyTypes: omitir a chave quando ausente, em vez de
    // retornar `{ token: undefined }`. Senão o tipo vira `string | undefined`
    // e contamina o tipo global de search das outras rotas.
    const out: { token?: string } = {};
    if (typeof search.token === 'string') out.token = search.token;
    return out;
  },
});

const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify-email',
  component: VerifyEmailPage,
  validateSearch: (search: Record<string, unknown>): { token?: string } => {
    const out: { token?: string } = {};
    if (typeof search.token === 'string') out.token = search.token;
    return out;
  },
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: DashboardPage,
});

const connectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/connections',
  component: ConnectionsPage,
});

const connectionDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/connections/$id',
  component: ConnectionDetailPage,
});

const templatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/templates',
  component: TemplatesPage,
});

const templateDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/templates/$id',
  component: TemplateDetailPage,
});

const messagesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/messages',
  component: MessagesPage,
  validateSearch: (
    search: Record<string, unknown>,
  ): { status?: string; period?: string; campaign?: string; page?: number } => {
    const out: { status?: string; period?: string; campaign?: string; page?: number } = {};
    if (typeof search.status === 'string') out.status = search.status;
    if (typeof search.period === 'string') out.period = search.period;
    if (typeof search.campaign === 'string') out.campaign = search.campaign;
    if (typeof search.page === 'number') out.page = search.page;
    else if (typeof search.page === 'string') {
      const parsed = Number.parseInt(search.page, 10);
      if (Number.isFinite(parsed)) out.page = parsed;
    }
    return out;
  },
});

const messageDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/messages/$id',
  component: MessageDetailPage,
});

const inboxRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inbox',
  component: InboxPage,
  validateSearch: (search: Record<string, unknown>): { conversation?: string } => {
    const out: { conversation?: string } = {};
    if (typeof search.conversation === 'string') out.conversation = search.conversation;
    return out;
  },
});

const leadsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/leads',
  component: LeadsPage,
});

const leadDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/leads/$id',
  component: LeadDetailPage,
});

const cadencesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cadences',
  component: CadencesPage,
});

const cadenceDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cadences/$id',
  component: CadenceDetailPage,
});

const campaignsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/campaigns',
  component: CampaignsPage,
});

const campaignDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/campaigns/$id',
  component: CampaignDetailPage,
});

const aiAgentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ai-agents',
  component: AIAgentsPage,
});

const aiAgentDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ai-agents/$id',
  component: AIAgentDetailPage,
});

const webhooksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/webhooks',
  component: WebhooksPage,
  validateSearch: (search: Record<string, unknown>): { tab?: string } => {
    const out: { tab?: string } = {};
    if (typeof search.tab === 'string') out.tab = search.tab;
    return out;
  },
});

const analyticsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/analytics',
  component: AnalyticsPage,
});

const businessManagersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/business-managers',
  component: BusinessManagersPage,
});

const businessManagerDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/business-managers/$id',
  component: BusinessManagerDetailPage,
});

const registrationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/registrations',
  component: RegistrationsPage,
});

// ─── Account (nested com sub-tabs) ───────────────────────────────────
const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account',
  component: AccountShellPage,
});

const accountIndexRoute = createRoute({
  getParentRoute: () => accountRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/account/profile' });
  },
});

const accountProfileRoute = createRoute({
  getParentRoute: () => accountRoute,
  path: 'profile',
  component: AccountProfilePage,
});

const accountSecurityRoute = createRoute({
  getParentRoute: () => accountRoute,
  path: 'security',
  component: AccountSecurityPage,
});

const accountTenantRoute = createRoute({
  getParentRoute: () => accountRoute,
  path: 'tenant',
  component: AccountTenantPage,
});

const accountIntegrationsRoute = createRoute({
  getParentRoute: () => accountRoute,
  path: 'integrations',
  component: AccountIntegrationsPage,
});

const accountWebhookSecretsRoute = createRoute({
  getParentRoute: () => accountRoute,
  path: 'webhook-secrets',
  // Bookmark antigo → redireciona pra rota consolidada (tab interna de Integrações).
  beforeLoad: () => {
    throw redirect({ to: '/account/integrations' });
  },
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  signupRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  verifyEmailRoute,
  indexRoute,
  connectionsRoute,
  connectionDetailRoute,
  templatesRoute,
  templateDetailRoute,
  messagesRoute,
  messageDetailRoute,
  inboxRoute,
  leadsRoute,
  leadDetailRoute,
  cadencesRoute,
  cadenceDetailRoute,
  campaignsRoute,
  campaignDetailRoute,
  aiAgentsRoute,
  aiAgentDetailRoute,
  webhooksRoute,
  analyticsRoute,
  businessManagersRoute,
  businessManagerDetailRoute,
  registrationsRoute,
  accountRoute.addChildren([
    accountIndexRoute,
    accountProfileRoute,
    accountSecurityRoute,
    accountTenantRoute,
    accountIntegrationsRoute,
    accountWebhookSecretsRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // Fallback de erro pra rotas que jogam exception não tratada. Substitui o
  // "Something went wrong" genérico do TanStack por UI com botão "Tentar
  // novamente" (qc.clear + router.invalidate) e link pro Dashboard.
  defaultErrorComponent: ({ error, reset }) => <RouteErrorPage error={error} reset={reset} />,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
